import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import MoonbucksLogo from "./MoonbucksLogo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = (path) =>
    `text-sm font-medium transition-colors ${loc.pathname === path ? "text-mb-emerald" : "text-neutral-600 hover:text-mb-emerald"}`;

  const mobileLinkCls = (path) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${
      loc.pathname === path
        ? "bg-mb-emerald/10 text-mb-emerald"
        : "text-neutral-700 hover:bg-neutral-100"
    }`;

  const closeAnd = (fn) => () => { setOpen(false); fn && fn(); };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-full bg-mb-emerald flex items-center justify-center text-white">
            <MoonbucksLogo className="w-5 h-5" />
          </div>
          <div className="font-serif-hero text-2xl leading-none">Moonbucks</div>
          <span className="hidden sm:inline-block text-xs text-neutral-400 mt-1">/ alerts</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {user ? (
            <>
              <Link to="/app" className={linkCls("/app")} data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/app/backtest" className={linkCls("/app/backtest")} data-testid="nav-backtest">Backtest</Link>
              {/* Screener is a subscriber-plan feature (promised in the Weekly + Monthly
                  bullets). Any subscribed user gets the link; leads / non-subs don't. */}
              {(user.role === "admin" || user.role === "subscriber" || user.subscribed === true) && (
                <Link to="/app/screener" className={linkCls("/app/screener")} data-testid="nav-screener">Screener</Link>
              )}
              <Link to="/app/account" className={linkCls("/app/account")} data-testid="nav-account">Account</Link>
              <Link to="/app/settings" className={linkCls("/app/settings")} data-testid="nav-settings">Settings</Link>
              {user.role === "admin" && (
                <Link to="/admin/dashboard" className="text-sm font-medium text-mb-emerald flex items-center gap-1" data-testid="nav-admin">
                  <ShieldCheck className="w-4 h-4" /> Desk
                </Link>
              )}
            </>
          ) : (
            <>
              <a href="#how" className="text-sm font-medium text-neutral-600 hover:text-mb-emerald">How it works</a>
              <a href="#pricing" className="text-sm font-medium text-neutral-600 hover:text-mb-emerald">Pricing</a>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <span className="hidden sm:inline text-xs text-neutral-500" data-testid="user-email">{user.email}</span>
              <Button
                size="sm"
                variant="ghost"
                className="hidden md:inline-flex text-neutral-600 hover:text-mb-emerald"
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/login")}
                className="hidden md:inline-flex"
                data-testid="login-btn-nav"
              >
                Login
              </Button>
              <Button
                size="sm"
                className="hidden md:inline-flex bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-full px-5"
                onClick={() => nav("/register")}
                data-testid="signup-btn-nav"
              >
                Get Moonbucks
              </Button>
            </>
          )}

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="md:hidden p-2 h-9 w-9"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 sm:w-80 bg-mb-sand p-0 flex flex-col" data-testid="mobile-menu-panel">
              <SheetTitle className="sr-only">Moonbucks navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Main navigation menu for Moonbucks alerts.
              </SheetDescription>

              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-neutral-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-mb-emerald flex items-center justify-center text-white">
                    <MoonbucksLogo className="w-4 h-4" />
                  </div>
                  <div className="font-serif-hero text-2xl leading-none">Moonbucks</div>
                </div>
                {user && (
                  <div className="mt-3 text-xs text-neutral-500 truncate" data-testid="mobile-user-email">
                    {user.email}
                  </div>
                )}
              </div>

              {/* Links */}
              <nav className="flex-1 px-3 py-4 space-y-1">
                {user ? (
                  <>
                    <Link to="/app" className={mobileLinkCls("/app")} onClick={closeAnd()} data-testid="mobile-nav-dashboard">Dashboard</Link>
                    <Link to="/app/backtest" className={mobileLinkCls("/app/backtest")} onClick={closeAnd()} data-testid="mobile-nav-backtest">Backtest</Link>
                    {(user.role === "admin" || user.role === "subscriber" || user.subscribed === true) && (
                      <Link to="/app/screener" className={mobileLinkCls("/app/screener")} onClick={closeAnd()} data-testid="mobile-nav-screener">Screener</Link>
                    )}
                    <Link to="/app/account" className={mobileLinkCls("/app/account")} onClick={closeAnd()} data-testid="mobile-nav-account">Account</Link>
                    <Link to="/app/settings" className={mobileLinkCls("/app/settings")} onClick={closeAnd()} data-testid="mobile-nav-settings">Settings</Link>
                    {user.role === "admin" && (
                      <Link to="/admin/dashboard" className={mobileLinkCls("/admin/dashboard") + " text-mb-emerald"} onClick={closeAnd()} data-testid="mobile-nav-admin">
                        <ShieldCheck className="w-4 h-4" /> Desk
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <a href="/#how" className={mobileLinkCls("/_how")} onClick={closeAnd()}>How it works</a>
                    <a href="/#pricing" className={mobileLinkCls("/_pricing")} onClick={closeAnd()}>Pricing</a>
                    <Link to="/track-record" className={mobileLinkCls("/track-record")} onClick={closeAnd()}>Track record</Link>
                  </>
                )}
              </nav>

              {/* Footer actions */}
              <div className="px-4 pt-3 pb-6 border-t border-neutral-200 space-y-2">
                {user ? (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={closeAnd(logout)}
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={closeAnd(() => nav("/login"))}
                      data-testid="mobile-login-btn"
                    >
                      Login
                    </Button>
                    <Button
                      className="w-full rounded-full bg-mb-emerald hover:bg-mb-emerald-dark text-white"
                      onClick={closeAnd(() => nav("/register"))}
                      data-testid="mobile-signup-btn"
                    >
                      Get Moonbucks
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
